import { supabase } from "@/lib/supabase";
import { Database } from "@/lib/database.types";

type DailyUsageLimit =
  Database["public"]["Tables"]["daily_usage_limits"]["Row"];
type FriendTestingInvite =
  Database["public"]["Tables"]["friend_testing_invites"]["Row"];
type FriendTestingUsage =
  Database["public"]["Tables"]["friend_testing_usage"]["Row"];

export interface UsageStats {
  feature_id: string;
  daily_limit: number;
  current_usage: number;
  remaining_usage: number;
  usage_percentage: number;
}

export interface CreateInviteRequest {
  friend_email: string;
  friend_name?: string;
  daily_limit?: number;
  trial_days?: number;
}

export interface InviteResponse {
  invite_code: string;
  share_url: string;
  expires_at: string;
}

export class UsageManagementService {
  /**
   * Check if user can use a feature and increment usage if allowed
   */
  static async checkAndIncrementUsage(
    userId: string,
    featureId: string,
    dailyLimit: number = 10
  ): Promise<{ allowed: boolean; remaining: number; limit: number }> {
    try {
      const { data, error } = await supabase.rpc("check_and_increment_usage", {
        p_user_id: userId,
        p_feature_id: featureId,
        p_daily_limit: dailyLimit,
      });

      if (error) {
        console.error("Error checking usage:", error);
        return { allowed: false, remaining: 0, limit: dailyLimit };
      }

      // Get current usage stats
      const stats = await this.getUserUsageStats(userId);
      const featureStats = stats.find((s) => s.feature_id === featureId);

      return {
        allowed: data,
        remaining: featureStats?.remaining_usage || 0,
        limit: featureStats?.daily_limit || dailyLimit,
      };
    } catch (error) {
      console.error("Error in checkAndIncrementUsage:", error);
      return { allowed: false, remaining: 0, limit: dailyLimit };
    }
  }

  /**
   * Get usage statistics for a user
   */
  static async getUserUsageStats(userId: string): Promise<UsageStats[]> {
    try {
      const { data, error } = await supabase.rpc("get_user_usage_stats", {
        p_user_id: userId,
      });

      if (error) {
        console.error("Error getting usage stats:", error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error("Error in getUserUsageStats:", error);
      return [];
    }
  }

  /**
   * Create a friend testing invite
   */
  static async createFriendInvite(
    inviterId: string,
    request: CreateInviteRequest
  ): Promise<InviteResponse> {
    try {
      const inviteCode = this.generateInviteCode();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // 7 days from now

      const { data, error } = await supabase
        .from("friend_testing_invites")
        .insert({
          inviter_id: inviterId,
          friend_email: request.friend_email,
          friend_name: request.friend_name,
          invite_code: inviteCode,
          daily_limit: request.daily_limit || 5,
          trial_days: request.trial_days || 3,
          expires_at: expiresAt.toISOString(),
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Error creating invite: ${error.message}`);
      }

      return {
        invite_code: data.invite_code,
        share_url: `${window.location.origin}/friend-invite/${data.invite_code}`,
        expires_at: data.expires_at,
      };
    } catch (error) {
      console.error("Error in createFriendInvite:", error);
      throw error;
    }
  }

  /**
   * Validate and accept a friend testing invite
   */
  static async acceptFriendInvite(
    inviteCode: string,
    userId: string
  ): Promise<{ success: boolean; dailyLimit: number; trialDays: number }> {
    try {
      // Get the invite
      const { data: invite, error: inviteError } = await supabase
        .from("friend_testing_invites")
        .select("*")
        .eq("invite_code", inviteCode)
        .eq("status", "pending")
        .single();

      if (inviteError || !invite) {
        throw new Error("Código de invitación no válido o caducado");
      }

      // Check if invite is expired
      if (new Date() > new Date(invite.expires_at)) {
        throw new Error("Invitación caducada");
      }

      // Update invite status
      const { error: updateError } = await supabase
        .from("friend_testing_invites")
        .update({
          status: "accepted",
          accepted_at: new Date().toISOString(),
        })
        .eq("id", invite.id);

      if (updateError) {
        throw new Error(`Error accepting invite: ${updateError.message}`);
      }

      // Create trial subscription for the friend
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + invite.trial_days);

      const { error: subscriptionError } = await supabase
        .from("subscriptions")
        .insert({
          user_id: userId,
          plan_id: "friend_testing",
          plan_name: "Friend Testing",
          status: "active",
          is_trial: true,
          trial_started_at: new Date().toISOString(),
          trial_ends_at: trialEndsAt.toISOString(),
          price_per_month: 0,
          billing_cycle: "monthly",
          started_at: new Date().toISOString(),
          ends_at: trialEndsAt.toISOString(),
        });

      if (subscriptionError) {
        console.error("Error creating trial subscription:", subscriptionError);
        // Don't throw here as the invite was already accepted
      }

      return {
        success: true,
        dailyLimit: invite.daily_limit,
        trialDays: invite.trial_days,
      };
    } catch (error) {
      console.error("Error in acceptFriendInvite:", error);
      throw error;
    }
  }

  /**
   * Track friend testing usage
   */
  static async trackFriendTestingUsage(
    userId: string,
    inviteId: string,
    featureId: string,
    requestData: Record<string, unknown>,
    responseSummary?: string,
    tokensUsed?: number,
    costEstimate?: number
  ): Promise<void> {
    try {
      const { error } = await supabase.from("friend_testing_usage").insert({
        user_id: userId,
        invite_id: inviteId,
        feature_id: featureId,
        request_data: requestData,
        response_summary: responseSummary,
        tokens_used: tokensUsed,
        cost_estimate: costEstimate,
      });

      if (error) {
        console.error("Error tracking friend testing usage:", error);
      }
    } catch (error) {
      console.error("Error in trackFriendTestingUsage:", error);
    }
  }

  /**
   * Get friend testing usage statistics
   */
  static async getFriendTestingStats(userId: string): Promise<{
    totalInvites: number;
    acceptedInvites: number;
    totalUsage: number;
    totalCost: number;
  }> {
    try {
      // Get invites created by user
      const { data: invites, error: invitesError } = await supabase
        .from("friend_testing_invites")
        .select("*")
        .eq("inviter_id", userId);


      if (invitesError) {
        console.error("Error getting invites:", invitesError);
        return {
          totalInvites: 0,
          acceptedInvites: 0,
          totalUsage: 0,
          totalCost: 0,
        };
      }

      // Get usage for accepted invites
      const acceptedInviteIds =
        invites
          ?.filter((invite) => invite.status === "accepted")
          .map((invite) => invite.id) || [];

      let totalUsage = 0;
      let totalCost = 0;

      if (acceptedInviteIds.length > 0) {
        const { data: usage, error: usageError } = await supabase
          .from("friend_testing_usage")
          .select("tokens_used, cost_estimate")
          .in("invite_id", acceptedInviteIds);

        if (!usageError && usage) {
          totalUsage = usage.reduce(
            (sum, record) => sum + (record.tokens_used || 0),
            0
          );
          totalCost = usage.reduce(
            (sum, record) => sum + (record.cost_estimate || 0),
            0
          );
        }
      }

      return {
        totalInvites: invites?.length || 0,
        acceptedInvites: acceptedInviteIds.length,
        totalUsage,
        totalCost,
      };
    } catch (error) {
      console.error("Error in getFriendTestingStats:", error);
      return {
        totalInvites: 0,
        acceptedInvites: 0,
        totalUsage: 0,
        totalCost: 0,
      };
    }
  }

  /**
   * Get user's active friend testing invite
   */
  static async getUserFriendInvite(
    userId: string
  ): Promise<FriendTestingInvite | null> {
    try {
      const { data, error } = await supabase
        .from("friend_testing_invites")
        .select("*")
        .eq("friend_email", userId) // Assuming friend_email is used to track who accepted
        .eq("status", "accepted")
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error getting user friend invite:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Error in getUserFriendInvite:", error);
      return null;
    }
  }

  /**
   * Generate a unique invite code
   */
  private static generateInviteCode(): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Reset daily usage limits (admin function)
   */
  static async resetDailyUsageLimits(): Promise<void> {
    try {
      const { error } = await supabase.rpc("reset_daily_usage_limits");
      if (error) {
        console.error("Error resetting daily usage limits:", error);
      }
    } catch (error) {
      console.error("Error in resetDailyUsageLimits:", error);
    }
  }
}
